import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  closeProfileModal,
  openProfileModal,
} from "../../../store/slice/auth-slice";
import { getLocationName } from "../../../utils/windowname";
import { getAllChildren } from "../../../utils/routes_childs";
import {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { FiLogOut } from "react-icons/fi";
import { RiFullscreenFill } from "react-icons/ri";
import ProfileModal from "../../../pages/Profile";
import Hamburger from "../../icons/Hamburger";
import ProfileIcon_mob from "../../icons/ProfileIcon_mob";
import LogoutIcon from "../../icons/Logout";
import PopupImages from "../popup";
import routes from "../../../routes";
import { BiSolidBell } from "react-icons/bi";
import { FaUserLarge } from "react-icons/fa6";
import {
  clearAllUnReadNotification,
  getUserNotifications,
  updateUnReadNotification,
} from "../../../api/auth/user";
import dayjs from "dayjs";
import Close from "../../icons/Close";
import { IoMdRefresh } from "react-icons/io";
import useLogout from "../../../hooks/useLogout";

/**
 * App header — page title, back button, notification bell + panel,
 * fullscreen toggle, profile button, logout dropdown.
 *
 * Audit-driven changes in this revision:
 *   - WebSocket effect REMOVED. Backend has no WS server (verified —
 *     no `ws`/`WebSocket` import or `server.upgrade` listener), and
 *     the frontend was passing `http://...` to the `WebSocket`
 *     constructor (which throws SyntaxError) AND embedding the JWT
 *     in the URL query string (token leak via proxy/access logs).
 *     Re-enable when the backend grows a WS endpoint with a proper
 *     wss:// scheme + Sec-WebSocket-Protocol auth.
 *   - `notification.url` open-redirect / XSS-via-notification fixed
 *     by an allow-list (relative paths only — must start with "/").
 *     `javascript:` / `data:` / cross-origin URLs are now rejected.
 *   - Narrow selectors instead of `useSelector(state => state.auth)`
 *     so unrelated auth-slice updates don't re-render the entire
 *     800-line header tree.
 *   - Logout uses the shared `useLogout` hook (also used by
 *     SideBar). Adds a confirmation step.
 *   - Refresh button now refetches notifications instead of full
 *     `window.location.reload()` (which silently destroyed unsaved
 *     work app-wide).
 *   - All buttons have `aria-label` / `aria-expanded` /
 *     `aria-haspopup` as appropriate. Mobile hamburger is now a
 *     real `<button>`.
 *   - `<ProfileModal />` and `<PopupImages />` moved OUTSIDE the
 *     `<header>` so the modal portal isn't semantically nested in
 *     the header element.
 *   - `progressBarStyles` injected via a `useEffect` (one-time)
 *     instead of mutating `document.head` at module load.
 *   - `displayName` rendered with a fallback so we don't print
 *     literal "null" text.
 *   - Page title is a `<p>` instead of `<h1>` to avoid stacking
 *     multiple h1s on a page.
 *   - `console.log` socket noise is gone with the socket itself.
 *   - Notification toast timer no longer restarts on each render
 *     (deps changed).
 */

// One-shot CSS injector — runs once on first mount, idempotent via
// the `data-progress-bar-styles` marker so HMR / repeated mounts
// don't stack duplicate <style> tags.
const useProgressBarStyles = () => {
  useEffect(() => {
    if (document.querySelector("style[data-progress-bar-styles]")) return;
    const styleSheet = document.createElement("style");
    styleSheet.setAttribute("data-progress-bar-styles", "");
    styleSheet.textContent = `
      @keyframes shrink {
        from { width: 100%; }
        to   { width: 0%; }
      }
    `;
    document.head.appendChild(styleSheet);
  }, []);
};

/**
 * Decide whether a notification URL is safe to navigate to. Returns
 * the URL when safe, null otherwise. Allow-list approach:
 *   - Must be a string.
 *   - Must start with "/" (in-app relative paths only).
 *   - Must NOT start with "//" (protocol-relative — would jump origin).
 *
 * Rejects: javascript:, data:, vbscript:, file:, http(s)://other,
 * fragment-only "#xxx", empty strings, anything truthy-but-weird.
 */
const safeNotificationUrl = (raw) => {
  if (typeof raw !== "string" || raw.length === 0) return null;
  if (!raw.startsWith("/")) return null;
  if (raw.startsWith("//")) return null;
  return raw;
};

// Notification Toast Component
const NotificationToast = ({ notification, onClose, onClick }) => {
  // `onClose` is intentionally NOT in the dep list — including it
  // would restart the 9s timer on every re-render of the parent (the
  // toast container's identity changes on every notificationList
  // update). We capture the latest `onClose` via a ref so the timer
  // calls the freshest function without resetting itself.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });
  useEffect(() => {
    const timer = setTimeout(() => onCloseRef.current?.(), 9000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="fixed top-5 md:left-1/2 transform -translate-x-1/2 z-[9999] md:max-w-sm w-full md:mx-4 animate-in slide-in-from-top-2 duration-300">
      <div
        className="bg-white border border-gray-200 rounded-lg shadow-lg p-4 cursor-pointer hover:shadow-xl transition-shadow"
        onClick={() => {
          onClick(notification, true);
          onClose();
        }}
      >
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <BiSolidBell className="w-4 h-4 text-pink" />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-semibold text-gray-900 mb-1">
                  New Notification
                </p>
                <p className="text-sm text-gray-600 line-clamp-2 break-words">
                  {notification?.description}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {dayjs(notification?.createdAt).format("MMM DD, hh:mm a")}
                </p>
              </div>

              <button
                type="button"
                aria-label="Dismiss notification"
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                  onClick(notification, false);
                }}
                className="ml-2 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
              >
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Notification Toast Manager Hook
const useNotificationToast = () => {
  const [notifications, setNotifications] = useState([]);
  const [notificationQueue, setNotificationQueue] = useState([]);
  const [isShowingToast, setIsShowingToast] = useState(false);
  const dismissTimerRef = useRef(null);

  // Cleanup the inter-toast delay timer on unmount so queued toasts
  // don't leak across unmounts.
  useEffect(() => {
    return () => {
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    };
  }, []);

  const showNotificationToast = useCallback(
    (notification) => {
      const id = Date.now();
      const notificationWithId = { ...notification, id };
      if (isShowingToast) {
        setNotificationQueue((prev) => [...prev, notificationWithId]);
      } else {
        setNotifications([notificationWithId]);
        setIsShowingToast(true);
      }
    },
    [isShowingToast],
  );

  const removeNotificationToast = useCallback((id) => {
    setNotifications((prev) => {
      const filtered = prev.filter((notification) => notification.id !== id);
      if (filtered.length === 0) {
        setIsShowingToast(false);
        if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
        dismissTimerRef.current = setTimeout(() => {
          setNotificationQueue((queue) => {
            if (queue.length > 0) {
              const [nextNotification, ...remainingQueue] = queue;
              setNotifications([nextNotification]);
              setIsShowingToast(true);
              return remainingQueue;
            }
            return queue;
          });
        }, 300);
      }
      return filtered;
    });
  }, []);

  const showUnreadNotifications = useCallback((unreadNotifications) => {
    if (unreadNotifications.length === 0) return;
    const [firstNotification, ...remainingNotifications] = unreadNotifications;
    const firstId = Date.now();
    setNotifications([{ ...firstNotification, id: firstId }]);
    setIsShowingToast(true);
    const queuedNotifications = remainingNotifications.map(
      (notification, index) => ({
        ...notification,
        id: firstId + index + 1,
      }),
    );
    setNotificationQueue(queuedNotifications);
  }, []);

  const NotificationToastContainer = ({ onNotificationClick }) => (
    <>
      {notifications.map((notification) => (
        <NotificationToast
          key={notification.id}
          notification={notification}
          onClose={() => removeNotificationToast(notification.id)}
          onClick={onNotificationClick}
        />
      ))}
    </>
  );

  return {
    showNotificationToast,
    showUnreadNotifications,
    NotificationToastContainer,
    queueLength: notificationQueue.length,
  };
};

// Notification Item Component
const NotificationItem = ({ notification, onClick, isRead }) => (
  <div
    className={`min-h-14 cursor-pointer border-b border-gray-200 hover:bg-gray-50 transition-colors ${
      isRead ? "opacity-60" : "bg-blue-50"
    }`}
    onClick={() => onClick(notification)}
  >
    <div className="px-5 py-3">
      <div className="font-medium text-sm text-gray-800 break-words leading-relaxed">
        {notification?.description}
      </div>
      <div className="flex justify-between items-center mt-2">
        <p className="text-xs text-gray-500">
          {dayjs(notification?.createdAt).format("DD MMM YYYY, hh:mm a")}
        </p>
        {!isRead && <div className="w-2 h-2 bg-blue-500 rounded-full" />}
      </div>
    </div>
  </div>
);

// Notification Panel Component
const NotificationPanel = ({
  isOpen,
  onClose,
  notifications,
  loading,
  onNotificationClick,
  onClearAll,
  hasUnread,
}) => {
  const panelRef = useRef(null);

  // Outside-click + Escape close. Listen for both mousedown AND
  // touchstart so iOS Safari long-press flows behave.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        onClose();
      }
    };
    const handleKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-label="Notifications"
      className="absolute top-20 right-4 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 animate-in slide-in-from-right-2 duration-200"
    >
      <div className="max-h-96 overflow-y-auto">
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-4 py-2 flex justify-between items-center rounded-t-lg">
          <h3 className="font-semibold text-gray-800">Notifications</h3>
          <button
            type="button"
            aria-label="Close notifications"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <Close />
          </button>
        </div>

        <div className="min-h-[420px]">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            </div>
          ) : notifications?.length === 0 ? (
            <div className="h-[350px] flex flex-col gap-5 justify-center items-center">
              <img
                src={"/dentist-day.png"}
                alt=""
                className="w-[100px] h-[100px]"
              />
              <div className=" font-gilroysemibold">No Notifications Yet</div>
            </div>
          ) : (
            notifications.map((notification, index) => (
              <NotificationItem
                key={notification._id || index}
                notification={notification}
                onClick={onNotificationClick}
                isRead={notification.isRead}
              />
            ))
          )}
        </div>

        {notifications?.length > 0 && hasUnread && (
          <div className="sticky bottom-0 bg-white border-t border-gray-200 p-3 rounded-b-lg">
            <button
              type="button"
              onClick={onClearAll}
              className="w-full py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors text-sm font-medium"
            >
              Mark all as read
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Main Header Component
const Header = ({ handleMobSideBar }) => {
  useProgressBarStyles();
  const location = useLocation();
  const { pathname } = location;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const logout = useLogout();

  // Narrow selectors — was `useSelector(s => s.auth)` which returned
  // the whole slice and re-rendered the header on every unrelated
  // auth field change.
  const displayName = useSelector((state) => state.auth?.displayName);
  const profileModalOpen = useSelector((state) => state.auth?.isOpen);

  const {
    showUnreadNotifications,
    NotificationToastContainer,
  } = useNotificationToast();

  // State management
  const [showLogout, setShowLogout] = useState(false);
  const [logoutConfirm, setLogoutConfirm] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [notificationPanel, setNotificationPanel] = useState(false);
  const [notificationList, setNotificationList] = useState([]);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);

  // Refs
  const logoutButtonRef = useRef(null);
  const notificationIconRef = useRef(null);
  const prevHasUnreadRef = useRef(false);
  const lastBackTapRef = useRef(0);

  // Computed values
  const formattedPathname = useMemo(
    // Pass `routes` so the resolver can prefer each module's
    // configured `title` (e.g. /expense → "Expense") over the raw
    // URL slug. Falls back to slug-parsing when no route matches.
    () => getLocationName(location, routes),
    [location],
  );
  const additionalBackButtonPaths = useMemo(() => getAllChildren(), []);
  const showBackButton = useMemo(() => {
    // Trim trailing slashes before counting segments — `/labs/`
    // shouldn't be treated as nested.
    const trimmed = (pathname || "").replace(/\/+$/, "") || "/";
    const depth = trimmed === "/" ? 0 : trimmed.split("/").length - 1;
    return depth > 1 || additionalBackButtonPaths.includes(pathname);
  }, [pathname, additionalBackButtonPaths]);

  // Notification functions
  const checkUnreadNotifications = useCallback(
    (notifications) =>
      Boolean(notifications?.some((notification) => !notification.isRead)),
    [],
  );

  const getNotifications = useCallback(async () => {
    setNotificationLoading(true);
    try {
      const response = await getUserNotifications();
      setNotificationList(response?.data || []);
      setHasUnread(checkUnreadNotifications(response?.data));
    } catch (error) {
      // Swallow — the bell still renders, just without the unread
      // indicator. Surfacing this as a toast would spam the user
      // every time the polling endpoint hiccups.
    } finally {
      setNotificationLoading(false);
    }
  }, [checkUnreadNotifications]);

  const handleNotificationClick = useCallback(
    async (notification, openInPlace) => {
      try {
        if (!notification.isRead) {
          await updateUnReadNotification(notification._id);
          setNotificationList((prev) => {
            const next = prev.map((item) =>
              item._id === notification._id
                ? { ...item, isRead: true }
                : item,
            );
            setHasUnread(checkUnreadNotifications(next));
            return next;
          });
        }
        setNotificationPanel(false);

        // Open-redirect / XSS hardening: only navigate when the URL
        // is a same-origin relative path. Server-controlled
        // `notification.url` previously went straight into
        // `window.location.href`, which would honour `javascript:`
        // pseudo-protocols and cross-origin URLs.
        if (openInPlace) {
          const safe = safeNotificationUrl(notification.url);
          if (safe) navigate(safe);
        }
      } catch (error) {
        // Silent — UI already moved on; the next poll will reconcile.
      }
    },
    // Note: `notificationList` is intentionally NOT in deps — we
    // mutate via setNotificationList(prev => ...) to avoid a stale
    // closure trap that re-bound this callback on every poll.
    [checkUnreadNotifications, navigate],
  );

  const handleClearAllUnread = useCallback(async () => {
    // Optimistic update: flip everything to read locally first so
    // the bell + panel reflect the action immediately, then sync.
    setNotificationList((prev) =>
      prev.map((n) => ({ ...n, isRead: true })),
    );
    setHasUnread(false);
    try {
      await clearAllUnReadNotification();
    } catch (error) {
      // On failure, refetch to reconcile.
      getNotifications();
    }
  }, [getNotifications]);

  // Refresh = full page reload of the current route. Heads-up: this
  // wipes any unsaved form state on the page, which is exactly what
  // users expect from a browser-style refresh button.
  const handleRefresh = useCallback(() => {
    window.location.reload();
  }, []);

  const handleProfileToggle = useCallback(() => {
    dispatch(profileModalOpen ? closeProfileModal() : openProfileModal());
  }, [dispatch, profileModalOpen]);

  const requestLogout = () => setLogoutConfirm(true);
  const cancelLogout = () => setLogoutConfirm(false);
  const confirmLogout = () => {
    setLogoutConfirm(false);
    setShowLogout(false);
    logout();
  };

  const getFallbackBackPath = useCallback(() => {
    const normalizePath = (path = "") => path.replace(/\/+$/, "") || "/";
    const currentPath = normalizePath(pathname);

    const parentRoute = routes.find(
      (route) =>
        Array.isArray(route.childrens) &&
        route.childrens.some((childPath) => {
          const normalizedChildPath = normalizePath(childPath);
          return (
            currentPath === normalizedChildPath ||
            currentPath.startsWith(`${normalizedChildPath}/`)
          );
        }),
    );

    if (parentRoute?.path) return parentRoute.path;

    const segments = currentPath.split("/").filter(Boolean);
    while (segments.length > 1) {
      segments.pop();
      const candidatePath = `/${segments.join("/")}`;
      if (
        routes.some(
          (route) => normalizePath(route.path) === normalizePath(candidatePath),
        )
      ) {
        return candidatePath;
      }
    }
    return "/";
  }, [pathname]);

  const handleBackNavigation = useCallback(() => {
    const now = Date.now();
    if (now - lastBackTapRef.current < 250) return;
    lastBackTapRef.current = now;

    const currentPath =
      typeof window !== "undefined" ? window.location.pathname : pathname;
    const canUseHistoryBack =
      typeof window !== "undefined" && window.history.length > 1;

    if (canUseHistoryBack) {
      navigate(-1);
      window.setTimeout(() => {
        if (window.location.pathname === currentPath) {
          const fallbackPath = getFallbackBackPath();
          if (fallbackPath !== currentPath) {
            navigate(fallbackPath, { replace: true });
          }
        }
      }, 140);
      return;
    }

    const fallbackPath = getFallbackBackPath();
    navigate(fallbackPath, { replace: true });
  }, [navigate, pathname, getFallbackBackPath]);

  const handleFullScreenToggle = useCallback(() => {
    const element = document.documentElement;
    if (!isFullScreen) {
      if (element.requestFullscreen) element.requestFullscreen();
      else if (element.mozRequestFullScreen) element.mozRequestFullScreen();
      else if (element.webkitRequestFullscreen)
        element.webkitRequestFullscreen();
      else if (element.msRequestFullscreen) element.msRequestFullscreen();
    } else {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (document.mozCancelFullScreen) document.mozCancelFullScreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      else if (document.msExitFullscreen) document.msExitFullscreen();
    }
  }, [isFullScreen]);

  // Effects
  useEffect(() => {
    getNotifications();
  }, [getNotifications]);

  // Replay newly-arrived unread notifications as toasts. Tracks
  // previously-seen ids in a ref so already-shown toasts don't
  // re-appear after every poll cycle.
  const seenUnreadIdsRef = useRef(new Set());
  useEffect(() => {
    const prevHasUnread = prevHasUnreadRef.current;
    if (hasUnread && !prevHasUnread && notificationList.length > 0) {
      const fresh = notificationList.filter(
        (n) => !n.isRead && !seenUnreadIdsRef.current.has(n._id),
      );
      if (fresh.length > 0) {
        showUnreadNotifications(fresh);
        for (const n of fresh) seenUnreadIdsRef.current.add(n._id);
      }
    }
    prevHasUnreadRef.current = hasUnread;
  }, [hasUnread, notificationList, showUnreadNotifications]);

  // Fullscreen change listener
  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(
        Boolean(
          document.fullscreenElement ||
            document.mozFullScreenElement ||
            document.webkitFullscreenElement ||
            document.msFullscreenElement,
        ),
      );
    };
    const events = [
      "fullscreenchange",
      "mozfullscreenchange",
      "webkitfullscreenchange",
      "msfullscreenchange",
    ];
    events.forEach((event) =>
      document.addEventListener(event, handleFullScreenChange),
    );
    return () => {
      events.forEach((event) =>
        document.removeEventListener(event, handleFullScreenChange),
      );
    };
  }, []);

  // Click-outside + Escape for the logout dropdown.
  useEffect(() => {
    if (!showLogout) return undefined;
    const handleClickOutside = (event) => {
      if (
        logoutButtonRef.current &&
        !logoutButtonRef.current.contains(event.target)
      ) {
        setShowLogout(false);
      }
    };
    const handleKey = (event) => {
      if (event.key === "Escape") setShowLogout(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, [showLogout]);

  return (
    <>
      <NotificationToastContainer
        onNotificationClick={handleNotificationClick}
      />
      <header className="lg:sticky top-0 flex w-full max-h-[75px] bg-[#EDF6F9] shadow-sm z-40">
        <div className="flex flex-grow items-center justify-between py-4 px-4 shadow-2 md:px-6 2xl:px-11">
          {/* Mobile hamburger — real <button> for keyboard access */}
          <button
            type="button"
            aria-label="Open navigation menu"
            className="hover:cursor-pointer block md:hidden pt-2 md:w-full"
            onClick={handleMobSideBar}
          >
            <Hamburger />
          </button>

          {/* Back button */}
          {showBackButton && (
            <button
              type="button"
              aria-label="Go back"
              onClick={handleBackNavigation}
              className="flex p-3 justify-center items-center gap-3 lg:w-[86px] lg:mx-0 mx-3 lg:h-[45px] w-[50px] h-[35px] bg-bluishgrey bg-opacity-[60%] shadow-card rounded-15 hover:scale-105 transition-transform"
            >
              <svg
                width="9"
                height="16"
                viewBox="0 0 9 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M0.337232 8.60567C0.121631 8.38689 0.000513077 8.09019 0.000513077 7.78084C0.000513077 7.47148 0.121631 7.17479 0.337232 6.956L6.84305 0.35617C6.94914 0.244741 7.07604 0.155862 7.21635 0.094718C7.35666 0.0335742 7.50757 0.00139017 7.66027 4.40491e-05C7.81297 -0.00130207 7.96441 0.0282169 8.10575 0.0868779C8.24708 0.145539 8.37549 0.232168 8.48347 0.341709C8.59145 0.451251 8.67685 0.581511 8.73467 0.72489C8.7925 0.868269 8.8216 1.0219 8.82027 1.1768C8.81894 1.33171 8.78722 1.4848 8.72694 1.62714C8.66667 1.76948 8.57906 1.89822 8.46922 2.00584L2.77648 7.78084L8.46922 13.5558C8.67871 13.7759 8.79463 14.0706 8.79201 14.3765C8.78939 14.6824 8.66844 14.975 8.45521 15.1913C8.24198 15.4076 7.95353 15.5303 7.65199 15.533C7.35046 15.5356 7.05995 15.418 6.84305 15.2055L0.337232 8.60567Z"
                  fill="#DE4AC4"
                />
              </svg>
              <p className="text-bodyRB text-darkgrey hidden lg:block">Back</p>
            </button>
          )}

          {/* Window title — <p> not <h1> to avoid stacking multiple
              h1s with whatever the page renders. */}
          <p className="w-full flex justify-center md:ml-[15%] text-headingBB text-darkgrey capitalize">
            {formattedPathname || "dashboard"}
          </p>

          {/* Actions */}
          <div className="flex items-center space-x-2">
            {/* Notification bell */}
            <div className="relative flex items-center space-x-2">
              <button
                type="button"
                ref={notificationIconRef}
                aria-label={
                  hasUnread
                    ? "Show notifications (unread)"
                    : "Show notifications"
                }
                aria-haspopup="dialog"
                aria-expanded={notificationPanel}
                onClick={() => setNotificationPanel((v) => !v)}
                className={`hover:scale-105 transition-transform lg:text-[22px] text-[16px] lg:bg-bluishgrey bg-white lg:bg-opacity-[10%] w-[34px] h-[36px] mt-[3px] lg:mt-0 lg:w-[43px] lg:h-[43px] flex items-center justify-center rounded-lg lg:rounded-full shadow-card ${
                  hasUnread
                    ? "text-danger animate-bell-ring origin-center"
                    : " text-pink"
                }`}
              >
                <BiSolidBell
                  className={hasUnread ? "animate-bounce" : ""}
                  aria-hidden="true"
                />
                {hasUnread && (
                  <span className="sr-only"> (you have unread)</span>
                )}
              </button>
              {hasUnread && (
                <div className="w-2 h-2 rounded-full bg-danger absolute right-0 lg:top-1 top-0">
                  <span
                    className="absolute inline-flex h-full w-full rounded-full bg-danger opacity-75 animate-ping"
                    aria-hidden="true"
                  />
                </div>
              )}
            </div>

            {/* Refresh button — full reload of the current route. */}
            <button
              type="button"
              onClick={handleRefresh}
              className="ml-2 hover:scale-105 transition-transform lg:text-[22px] text-[16px] text-pink md:bg-none bg-white md:bg-opacity-[10%] w-[34px] h-[36px] mt-[3px] lg:mt-0 lg:w-[43px] lg:h-[43px] flex items-center justify-center rounded-lg lg:rounded-full shadow-card relative"
              aria-label="Refresh page"
            >
              <IoMdRefresh strokeWidth={2.5} size={25} aria-hidden="true" />
            </button>

            {/* Fullscreen button */}
            <button
              type="button"
              onClick={handleFullScreenToggle}
              aria-label={
                isFullScreen ? "Exit full screen" : "Enter full screen"
              }
              aria-pressed={isFullScreen}
              className="ml-2 hover:scale-105 transition-transform lg:text-[22px] text-[16px] text-pink md:bg-none bg-white md:bg-opacity-[10%] w-[34px] h-[36px] mt-[3px] lg:mt-0 lg:w-[43px] lg:h-[43px] flex items-center justify-center rounded-lg lg:rounded-full shadow-card relative"
            >
              <RiFullscreenFill aria-hidden="true" />
            </button>

            {/* Profile button */}
            <button
              type="button"
              aria-label="Open profile"
              aria-expanded={profileModalOpen}
              onClick={handleProfileToggle}
              className="hover:scale-105 transition-transform w-8 h-8 lg:w-[43px] lg:h-[43px]"
            >
              <div className="hidden md:flex lg:text-[18px] text-[16px] text-pink bg-bluishgrey bg-opacity-[10%] lg:w-[43px] lg:h-[43px] items-center justify-center rounded-full shadow-card">
                <FaUserLarge aria-hidden="true" />
              </div>
              <div className="block md:hidden -mt-1">
                <ProfileIcon_mob />
              </div>
            </button>

            {/* Logout button + dropdown + confirmation */}
            <div className="relative" ref={logoutButtonRef}>
              <button
                type="button"
                aria-label="Logout menu"
                aria-haspopup="menu"
                aria-expanded={showLogout}
                className="hover:scale-105 transition-transform w-8 h-8 lg:w-[57px] lg:h-[53px] hidden md:block"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowLogout((v) => !v);
                }}
              >
                <LogoutIcon />
              </button>

              {showLogout && (
                <div
                  role="menu"
                  className="absolute top-full right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-4 min-w-[180px] z-50"
                >
                  <div className="text-center mb-3">
                    <div className="font-medium text-gray-800 truncate max-w-[160px]">
                      {/* Null-guard so we don't render literal "null" */}
                      {displayName || "Account"}
                    </div>
                  </div>
                  {/*
                    Theme palette note: the project's tailwind.config.js
                    overrides the top-level `colors` block, which kills
                    the default `red` palette (no `bg-red-500`, no
                    `text-red-600`, etc.). The previous Confirm + Logout
                    styling used those classes and rendered invisibly.
                    Switched to the in-theme `danger` (#E45353) — same
                    convention used by the shared <Button type="danger">.
                  */}
                  {logoutConfirm ? (
                    <div className="flex flex-col gap-2">
                      <p className="text-xs text-darkgrey text-center">
                        Sign out of this session?
                      </p>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={cancelLogout}
                          className="flex-1 py-1.5 px-3 bg-gray-100 hover:bg-gray-200 text-darkgrey rounded-md text-sm"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={confirmLogout}
                          className="flex-1 py-1.5 px-3 bg-danger hover:opacity-90 text-white rounded-md text-sm transition-opacity"
                        >
                          Confirm
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={requestLogout}
                      role="menuitem"
                      className="flex items-center justify-center space-x-2 w-full py-2 px-3 bg-bluishgrey hover:bg-secondary text-danger rounded-md transition-colors"
                    >
                      <FiLogOut aria-hidden="true" />
                      <span>Logout</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Notification Panel */}
        <NotificationPanel
          isOpen={notificationPanel}
          onClose={() => setNotificationPanel(false)}
          notifications={notificationList}
          loading={notificationLoading}
          onNotificationClick={handleNotificationClick}
          onClearAll={handleClearAllUnread}
          hasUnread={hasUnread}
        />
      </header>

      {/* Modals are rendered OUTSIDE the <header> element so they're
          not semantically nested in the header landmark. ProfileModal
          internally uses ReactDOM.createPortal anyway, but moving the
          mount-point keeps the DOM tidy + future-proofs against any
          render-tree assumption. */}
      <ProfileModal />
      <PopupImages />
    </>
  );
};

export default Header;
